import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  ChevronRight,
  MessageSquare,
  Phone,
  Bot,
  Clock,
  Filter,
} from 'lucide-react';
import RefreshButton from '../../components/layout/RefreshButton';
import { adminApi } from '../../services/adminApi';

export default function ConversationList() {
  const [searchParams] = useSearchParams();
  const agentIdFilter = searchParams.get('agent_id') || '';

  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [agentInfo, setAgentInfo] = useState(null);

  const fetchConversations = useCallback(async () => {
    try {
      let response;
      if (agentIdFilter) {
        response = await adminApi.getAgentConversations(agentIdFilter, {
          page,
          limit: 20,
        });
        setAgentInfo(response.agent);
      } else {
        response = await adminApi.getConversations({
          page,
          limit: 20,
        });
      }
      setConversations(response.conversations || []);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  }, [page, agentIdFilter]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await fetchConversations();
      setIsLoading(false);
    };
    loadData();
  }, [fetchConversations]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchConversations();
    setIsRefreshing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateMessage = (message, maxLength = 60) => {
    if (!message) return '-';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            {agentInfo ? `Conversations - ${agentInfo.name}` : 'All Conversations'}
          </h1>
          <p className="text-text-secondary mt-1">
            {agentIdFilter
              ? `View all conversations for this agent (${total} total)`
              : `View all conversations across the platform (${total} total)`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <RefreshButton
            onClick={handleRefresh}
            isLoading={isRefreshing}
            lastUpdated={lastUpdated}
          />
          {agentIdFilter && (
            <Link to="/conversations" className="btn-secondary">
              View All Conversations
            </Link>
          )}
        </div>
      </div>

      {/* Conversations List */}
      <div className="card">
        {isLoading ? (
          <div className="p-4">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-20 bg-bg-secondary rounded"></div>
              ))}
            </div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No conversations found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {conversations.map((conversation) => (
              <Link
                key={conversation.id}
                to={`/conversations/${conversation.id}`}
                className="p-4 flex items-start gap-4 hover:bg-bg-secondary/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <h3 className="font-semibold text-text-primary flex items-center gap-2">
                        {conversation.senderName || conversation.senderPhone || 'Unknown'}
                      </h3>
                      {conversation.senderName && conversation.senderPhone && (
                        <p className="text-xs text-text-muted flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {conversation.senderPhone}
                        </p>
                      )}
                      {!agentIdFilter && conversation.agentName && (
                        <p className="text-sm text-text-muted flex items-center gap-1 mt-1">
                          <Bot className="w-3 h-3" />
                          {conversation.agentName}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="badge badge-gray">
                        {conversation.messageCount || 0} messages
                      </span>
                      {conversation.unread && (
                        <span className="badge badge-warning">Unread</span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-text-secondary line-clamp-1">
                    {truncateMessage(conversation.lastMessage)}
                  </p>
                  <p className="text-xs text-text-muted mt-2 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Last activity: {formatDate(conversation.updatedAt)}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
